import React, { useEffect, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdDeleteForever, MdEdit, MdImage } from "react-icons/md";
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';

import { Alert, IconButton } from "@material-ui/core";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import swal from "@sweetalert/with-react";
import { Form } from "@unform/web";
import { toast } from "react-toastify";
import * as Yup from "yup";
import DivDiagonal from "../../components/DivDiagonal";
import CustomButton from "../../components/Form/Button";
import DeleteProductModal from "./DeleteProductModal";
import Input from "../../components/Form/Input";
import messageTypes from "../../constants/MessagesTypes";
import { getProducts, deleteProduct } from "../../services/Product";
import { currencyFormat } from "../../utils/masks";
import {
  ProductWrapper,
  PageHeader,
  NewProductButton,
  ProductCard,
  FilterSection,
  FilterTitle,
  StyledFilterFormWrapper,
  ContentArea,
  StyledTableContainer,
  ProductImage,
  ProductInfo,
  ActionButtons,
  PaginationInfo,
  PaginationButton,
  LoadingButton,
  EmptyState,
  TableWrapper,
  FilterRow,
  FilterInputWrapper,
  FilterButtonWrapper
} from "./styles";

export default function ProductNew() {
  const { currentCompany } = useSelector((state) => state.CompanyReducer);
  const [loading, setLoading] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);

  const formRef = useRef(null);

  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    stringSearch: '',
  });

  useEffect(() => {
    setLoading(true);
    
    // Resetar página quando a empresa mudar
    if (currentCompany?.id) {
      setPage(1);
    }

    getProducts({
      page: page,
      per_page: 25,
      startDate: filter.startDate,
      endDate: filter.endDate,
      stringSearch: filter.stringSearch,
    }).then((data) => {
      if (!data || data === '') {
        return;
      }

      setTotal(data.data.total);
      setProducts(data.data);
      setLoading(false);
    });

    return function cleanup() {
      setProducts([]);
    };
  }, [page, deleteProductId, filter, currentCompany?.id]);

  async function handleSubmit(data) {
    try {

      formRef.current.setErrors({});
      const schema = Yup.object().shape({});

      await schema.validate(data, {
        abortEarly: false,
      });

      setFilter({
        startDate: data.startDate,
        endDate: data.endDate,
        stringSearch: data.stringSearch,
      });

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        toast.warning(messageTypes.REQUIRED_FIELDS);
        return;
      }

      toast.error(messageTypes.ERROR);
    }
  }

  function showImage(urlImage) {
    swal(
      {
        showCloseButton: true,
        className: 'img-perfil-modal',
        confirmButtonText: 'Fechar',
        content: <div>
          <div className={"img-perfil"}>
            <img src={urlImage} width={'80%'} height={"80%"} alt={'Imagem do produto'}/>
          </div>
        </div>,
      }
    );
  }

  function handleDelete(id) {
    setDeleteProductId(id);
  }

  async function handleConfirmDelete() {
    try {
      await deleteProduct(deleteProductId);
      toast.success(messageTypes.SUCCESS_DELETE || 'Produto excluído com sucesso!');
      setDeleteProductId(null);
      // Recarregar a lista
      setLoading(true);
      getProducts({
        page: page,
        per_page: 25,
        startDate: filter.startDate,
        endDate: filter.endDate,
        stringSearch: filter.stringSearch,
      }).then((data) => {
        if (!data || data === '') {
          setProducts([]);
          setTotal(0);
          setLoading(false);
          return;
        }

        setTotal(data.data.total);
        setProducts(data.data);
        setLoading(false);
        // Ajustar página se necessário
        if (data.data && data.data.data && data.data.data.length === 0 && page > 1) {
          setPage(page - 1);
        }
      }).catch(() => {
        setProducts([]);
        setTotal(0);
        setLoading(false);
      });
    } catch (error) {
      toast.error(error.response?.data?.message || messageTypes.ERROR_DELETE || 'Erro ao excluir produto');
      setDeleteProductId(null);
    }
  }

  return (
    <ProductWrapper>
      <DivDiagonal/>
      <ContentArea>
        <PageHeader>
          <h1>
            <i className="fa fa-folder-open"/> Produtos <span className="badge">{total}</span>
          </h1>
          <NewProductButton as={Link} to="/product/new">
            <i className="fas fa-plus"/> Novo produto
          </NewProductButton>
        </PageHeader>
        
        <ProductCard>
          <FilterSection>
            <StyledFilterFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit}>
                <FilterTitle>
                  <i className="fas fa-funnel-dollar"/> Filtro
                </FilterTitle>
                <FilterRow>
                  <FilterInputWrapper>
                    <Input
                      type="text"
                      name="stringSearch"
                      placeholder={"Buscar por ID, nome ou código de barras"}
                      label={'Pesquisar produto'}
                    />
                  </FilterInputWrapper>
                  <FilterButtonWrapper>
                    <CustomButton
                      className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                      type="submit"
                      startIcon={<AiOutlineSearch/>}
                      style={{
                        background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                        border: 'none',
                        color: '#ffffff',
                        padding: '0 32px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                        transition: 'all 0.3s ease',
                        width: 'auto',
                        minWidth: 'auto',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                      }}
                    >
                      Buscar
                    </CustomButton>
                  </FilterButtonWrapper>
                </FilterRow>
              </Form>
            </StyledFilterFormWrapper>
          </FilterSection>
          <TableWrapper>
            {loading ? (
              <LoadingButton
                className="btn btn-primary text-capitalize font-weight-bold"
                loading={loading}
                type="button"
              >
                Carregando...
              </LoadingButton>
            ) : (
              <>
                {products && products.data && products.data.length ? (
                  <StyledTableContainer>
                    <Table sx={{minWidth: 650}} aria-label="caption table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Produto</TableCell>
                          <TableCell align="center">Preço de Custo</TableCell>
                          <TableCell align="center">Preço de Venda</TableCell>
                          <TableCell align="center">Opções</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {products.data.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px' }}>
                                <ProductImage
                                  onClick={() => {
                                    if (item.urlPhoto) {
                                      showImage(`${process.env.REACT_APP_API_ENDPOINT}${item.urlPhoto}`);
                                    }
                                  }}
                                >
                                  {item.urlPhoto ? (
                                    <img
                                      src={`${process.env.REACT_APP_API_ENDPOINT}${item.urlPhoto}`}
                                      alt={"Imagem do produto"}
                                    />
                                  ) : (
                                    <MdImage />
                                  )}
                                </ProductImage>
                                <ProductInfo>
                                  <div className="product-name">{item.name}</div>
                                  <div className="product-details">
                                    {item.barcode && `Código de barras: ${item.barcode}`}
                                    {item.barcode && item.brand && <br />}
                                    {item.brand && `Marca: ${item.brand}`}
                                  </div>
                                </ProductInfo>
                              </div>
                            </TableCell>
                            <TableCell align="center">
                              {currencyFormat(item.costPrice, 'R$')}
                            </TableCell>
                            <TableCell align="center">
                              {currencyFormat(item.salePrice, 'R$')}
                            </TableCell>
                            <TableCell align="center">
                              <ActionButtons>
                                <Link to={`/product/${item.id}/edit`}>
                                  <IconButton aria-label="edit">
                                    <MdEdit/>
                                  </IconButton>
                                </Link>
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  <MdDeleteForever/>
                                </IconButton>
                              </ActionButtons>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan="4" style={{ border: 'none' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                              <PaginationInfo>
                                Página {page} de {products.total_page + 1}
                              </PaginationInfo>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <PaginationButton
                                  type="button"
                                  onClick={() => setPage(page - 1)}
                                  disabled={page <= 1}
                                >
                                  <IoIosArrowBack/> Anterior
                                </PaginationButton>
                                <PaginationButton
                                  type="button"
                                  onClick={() => setPage(page + 1)}
                                  disabled={!products.next_page}
                                >
                                  Próxima <IoIosArrowForward/>
                                </PaginationButton>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </StyledTableContainer>
                ) : (
                  <EmptyState>
                    <Alert severity="warning">{messageTypes.REGISTER_NOT_FOUNT}</Alert>
                  </EmptyState>
                )}
              </>
            )}
          </TableWrapper>
        </ProductCard>
      </ContentArea>

      <DeleteProductModal
        isOpen={!!deleteProductId}
        onClose={() => setDeleteProductId(null)}
        onConfirm={handleConfirmDelete}
        productId={deleteProductId}
      />
    </ProductWrapper>
  );
}